import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { QCActionValue, SearchedList } from './types';
import { useStore } from '30.quickConnect.Stores';
import {
  AllFieldValueTypes,
  Choice,
  DeclarationContext,
  DialogDesc,
  FieldDesc,
  QcActionDesc,
} from '90.quickConnect.Models/models';
import { debounceTime } from '50.quickConnect.Fields/const';
import { FieldType, Mandatory } from '90.quickConnect.Models/enums';
import { useDeclarationContext } from '10.quickConnect.app/components/domain/Declaration/context/declarationContext';
import { getActionParamFromDeclaration } from '50.quickConnect.Fields/FieldsTypes/Others/QcAction/utils';
import { isEmptyFieldValue } from '90.quickConnect.Models/guards';

const normalizeChoices = (values: Choice | Choice[] | null | undefined): Choice[] => {
  const rawValues = Array.isArray(values) ? values : values ? [values] : [];
  return rawValues;
};

const useDataAction = (
  actionField: QcActionDesc & DialogDesc,
  setSelectedIndex: React.Dispatch<React.SetStateAction<string | undefined>>,
  context: DeclarationContext,
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  field: FieldDesc,
  open: string | undefined,
  flattenFields: FieldDesc[],
  handleSubmit: (_event: React.SyntheticEvent<Element, Event>) => void,
  handleCancel: (_event: React.SyntheticEvent<Element, Event>) => void,
  handleOpen: (_event: React.SyntheticEvent<Element, Event>) => void,
) => {
  const {
    DeclarationStore: { actionSearchData },
    QCScriptStore: { setControllerMethod, setQCSFieldId, setQCSBaseFieldId },
  } = useStore();
  const { fullPathId, value } = field;
  const [localValueForCheckBox, setLocalValueForCheckBox] = useState<Choice[]>([]);
  const [searchedList, setSearchedList] = useState<SearchedList>();
  const [pagination, setPagination] = useState<{ [key: number]: string }>();
  const [actionToken, setActionToken] = useState<string>('');
  const [localValue, setLocalValue] = useState<string>((value as Choice)?.value ?? '');
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [currentElementToDelete, setCurrentElementToDelete] = useState<string>();
  const [compteur, setCompteur] = useState<number>(0);
  const [validatedLocalValueForCheckBox, setValidatedLocalValueForCheckBox] = useState<Choice[]>([]); // Pour les sélections multiples
  const [validatedLocalValue, setValidatedLocalValue] = useState<string>(''); // Pour les sélections simples
  const [canQueryDetail, setCanQueryDetail] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const declaration = useDeclarationContext();

  const updateGlobalState = useCallback(() => {
    const selectedChoice = searchedList?.resultList.find((c) => c.value === localValue);
    let data;

    if (actionField.isMultiSelection) {
      data = [
        {
          fieldType: FieldType.CheckBoxList,
          id: actionField.id,
          label: actionField.label,
          value: localValueForCheckBox,
        },
      ];
    } else if (selectedChoice) {
      data = [{ fieldType: FieldType.RadioList, id: actionField.id, label: actionField.label, value: selectedChoice }];
    }
    if (isDelete) {
      const selectedValues = (actionField.value as QCActionValue[])?.[0]?.value;
      const normalizedSelectedValues = normalizeChoices(selectedValues as Choice | Choice[] | null | undefined);
      const x = normalizedSelectedValues.filter((v: Choice) => currentElementToDelete !== v.value);
      if (x.length === 0) {
        data = [];
      } else {
        data = [{ ...(actionField?.value as QCActionValue[])[0], value: x }];
      }
      setLocalValueForCheckBox((prev) => prev.filter((choice) => choice.value !== currentElementToDelete));
      setLocalValue('');
    }
    updateDeclaration(fullPathId, data as any);
    setIsDelete(false);
  }, [
    searchedList?.resultList,
    actionField.isMultiSelection,
    actionField.id,
    actionField.label,
    actionField?.value,
    isDelete,
    updateDeclaration,
    fullPathId,
    localValue,
    localValueForCheckBox,
    currentElementToDelete,
  ]);

  const debouncedUpdateGlobalState = useDebouncedCallback(() => {
    updateGlobalState();
  }, debounceTime);

  const initialSearch = useCallback(
    async () => {
      if (isSearching) return;

      setIsSearching(true);
      try {
        const body = {
          actionName: actionField.actionName,
          actionParam: {
            values: getActionParamFromDeclaration(flattenFields, fullPathId),
          },
          actionToken: '',
          formId: context.formId,
        };
        const res = await actionSearchData(body);
        if (res) {
          setActionToken(res.data.actionToken);
          setSearchedList(res.data);

          if (!actionField.isPopup) {
            const values = Array.isArray(res.data.resultList) ? res.data.resultList : [res.data.resultList];
            const normalizedValues = normalizeChoices(values as Choice | Choice[] | null | undefined);

            if (actionField.isMultiSelection) {
              setLocalValueForCheckBox(normalizedValues);
              actionField.value = [
                {
                  fieldType: FieldType.CheckBoxList,
                  id: actionField.id,
                  label: actionField.label ?? '',
                  value: normalizedValues,
                },
              ];
            } else {
              const [first] = normalizedValues;
              setLocalValue(first?.value ?? '');
              actionField.value = [
                {
                  fieldType: FieldType.RadioList,
                  id: actionField.id,
                  label: actionField.label ?? '',
                  value: first ? [first] : [],
                },
              ];
            }

            debouncedUpdateGlobalState();
          }
        }
      } finally {
        setIsSearching(false);
      }
    },
    // We don't want update on useCallback references so we don't subscribe to declaration which will update after each call
    // because the function is call on init, we shouldn't have dependencies delta
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actionField.actionName,
      actionField.id,
      actionField.label,
      context.formId,
      fullPathId,
      actionSearchData,
      // KEEP COMMENTED otherwise it will become an infinite loop
      // updateDeclaration,
      // declaration,
    ],
  );

  const preselectedChoices = useMemo(() => {
    if (!actionField.value) return [];
    const values = (actionField.value as QCActionValue[])?.[0]?.value;
    return normalizeChoices(values as Choice | Choice[] | null | undefined);
  }, [actionField.value]);

  useEffect(() => {
    if (open && open === fullPathId) {
      if (preselectedChoices.length > 0) {
        if (actionField.isMultiSelection) {
          setLocalValueForCheckBox(preselectedChoices);
          setValidatedLocalValueForCheckBox(preselectedChoices); // Sauvegarder aussi les valeurs validées
        } else {
          const initialValue = preselectedChoices[0]?.value ?? '';
          setLocalValue(initialValue);
          setValidatedLocalValue(initialValue); // Sauvegarder aussi la valeur validée
        }
      } else {
        // Pas de valeurs présélectionnées, initialiser à vide
        setLocalValueForCheckBox([]);
        setValidatedLocalValueForCheckBox([]);
        setLocalValue('');
        setValidatedLocalValue('');
      }
    }
  }, [open, fullPathId, preselectedChoices, actionField.isMultiSelection]);

  // Une clé de dépendance pour détecter les changements de valeurs dans les items
  const itemsValuesKey = actionField.items?.map((f) => `${f.value}|${f.errors?.length}`).join('||');

  const canSearch = useMemo(() => {
    if (!actionField.items || actionField.items.length === 0) return true;

    // Vérifier qu'il n'y a pas d'erreurs
    const hasErrors = actionField.items.some((f) => f.errors && f.errors?.length > 0);
    if (hasErrors) return false;

    // Vérifier que tous les items obligatoires sont remplis
    const hasEmptyMandatoryField = actionField.items.some(
      (f) =>
        f.mandatory === Mandatory.Required &&
        f.isVisible === true &&
        f.fieldType !== FieldType.CheckBox &&
        isEmptyFieldValue(f.value),
    );

    return !hasEmptyMandatoryField;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsValuesKey]);

  // Bloquer la recherche automatique (callOnStart) si les items obligatoires ne sont pas remplis
  useEffect(() => {
    if (actionField?.callOnStart && open && open === fullPathId && canSearch) {
      initialSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionField?.callOnStart, open, fullPathId]);

  const updateCheckBoxLocalState = useCallback(
    (choice: Choice) => () => {
      const safeChecked = localValueForCheckBox.filter((c): c is Choice => Boolean(c));
      const newChecked = [...safeChecked];
      const currentIndex = safeChecked.findIndex((c) => c.value === choice.value);
      if (currentIndex === -1) {
        newChecked.push(choice);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setLocalValueForCheckBox(newChecked);
    },
    [localValueForCheckBox],
  );
  const updateLocalState = useCallback((_event: React.ChangeEvent<HTMLInputElement>, newValue: string | Choice) => {
    setLocalValue(newValue as string);
  }, []);

  const resetFields = useCallback(() => {
    // Réinitialiser les valeurs des items
    if (actionField.items) {
      actionField.items.forEach((item) => {
        if ('defaultValue' in item && item.defaultValue !== undefined && item.defaultValue !== '') {
          return;
        }
        item.value = null;
      });
    }
    setSearchedList(undefined);
    setPagination(undefined);
  }, [actionField.items]);

  const handleSubmitWithReset = useCallback(
    async (_event: React.SyntheticEvent<Element, Event>) => {
      updateGlobalState();
      if (canQueryDetail) {
        const body = {
          actionName: actionField.actionName,
          actionParam: {
            values: null,
            selectedValues: [localValue],
          },
          actionToken: '',
          formId: context.formId,
        };
        const res = await actionSearchData(body);
        if (res) setSearchedList(res.data);
      }
      handleSubmit(_event);
      setValidatedLocalValueForCheckBox([...localValueForCheckBox]); // Mettre à jour l'état validé pour les sélections multiples
      setValidatedLocalValue(localValue); // Mettre à jour pour les sélections simples
    },
    [
      actionField.actionName,
      actionSearchData,
      canQueryDetail,
      context.formId,
      handleSubmit,
      localValue,
      localValueForCheckBox,
      updateGlobalState,
    ],
  );

  useEffect(() => {
    if (searchedList?.resultList && validatedLocalValue) {
      updateGlobalState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedList, validatedLocalValue, isDelete]);

  const handleCancelWithReset = useCallback(
    (_event: React.SyntheticEvent<Element, Event>) => {
      handleCancel(_event);
      setLocalValueForCheckBox([...validatedLocalValueForCheckBox]); // Réinitialiser à la dernière valeur validée
      setLocalValue(validatedLocalValue); // Réinitialiser à la dernière valeur validée
      resetFields(); // Réinitialisation des champs
    },
    [handleCancel, resetFields, validatedLocalValue, validatedLocalValueForCheckBox],
  );

  const execLocalAction = useCallback((): boolean => {
    if (actionField.actionName?.toLowerCase() === '_qcscriptrunner') {
      setControllerMethod('onQCActionClick');
      setQCSBaseFieldId(actionField.id);
      setQCSFieldId(actionField.id);
      return true;
    }
    // Sinon, si le caller fournit un nom de méthode, on le joue quand même
    return false;
  }, [actionField.actionName, actionField.id, setControllerMethod, setQCSBaseFieldId, setQCSFieldId]);

  const onActionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (actionField?.isPopup) {
        handleOpen(e);
        return;
      }

      // Si execLocalAction() renvoie false, on lance la recherche
      if (!execLocalAction()) {
        void initialSearch();
      }
    },
    [actionField?.isPopup, handleOpen, execLocalAction, initialSearch],
  );

  const handleSearch = useCallback(
    async (actionsToken?: string) => {
      if (isSearching) return;

      setIsSearching(true);
      try {
        const body = {
          actionName: actionField.actionName,
          actionParam: {
            values: getActionParamFromDeclaration(flattenFields, fullPathId),
          },
          actionToken: actionsToken ?? '',
          formId: context.formId,
        };
        const res = await actionSearchData(body);
        if (res) {
          setActionToken(res.data.actionToken);
          setSearchedList(res.data);
          setCanQueryDetail(res.data.canQueryDetail);
        }
      } finally {
        setIsSearching(false);
      }
    },
    [actionField.actionName, actionSearchData, context.formId, flattenFields, fullPathId, isSearching],
  );
  const handleNext = useCallback(async () => {
    const nextIndex = compteur + 1;
    setPagination({ ...pagination, [nextIndex]: actionToken });
    await handleSearch(actionToken);
    setCompteur(() => compteur + 1);
  }, [actionToken, compteur, handleSearch, pagination]);

  const handlePrevious = useCallback(async () => {
    const previousIndex = compteur - 1;

    await handleSearch(pagination![previousIndex]);
    setCompteur(() => compteur - 1);
  }, [compteur, handleSearch, pagination]);

  const handleDelete = useCallback(
    (currentElem: string) => {
      setLocalValue('');
      setIsDelete(true);
      setCurrentElementToDelete(currentElem);
      debouncedUpdateGlobalState();
    },
    [debouncedUpdateGlobalState],
  );
  return {
    handleSearch,
    searchedList,
    handleNext,
    handlePrevious,
    updateLocalState,
    localValue,
    compteur,
    canSearch,
    handleDelete,
    updateCheckBoxLocalState,
    localValueForCheckBox,
    handleSubmitWithReset,
    handleCancelWithReset,
    onActionClick,
    isSearching,
  };
};
export default useDataAction;
